import { bodyFont, colours } from "@affinity/constants/StyleConstants";
import { breakpointWidth, rem } from "@affinity/constants/StyleHelpers";
import { Section as QuoteWidget } from "@affinity/Widgets/Basic/Testimonial";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import Typography from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { FC, useCallback } from "react";
import styled from "styled-components";

interface OwnProps extends WidgetProps {
    fields: {
        heading?: string;
        link: LinkFieldValue;
        title: string;
        image: string;
        tag?: string;
        publishDate: Date;
    };
}

export const ArticleSection: FC<OwnProps> = ({ fields }) => {
    const link = useGetLink(fields.link);
    const history = useHistory();
    const onClick = useCallback(() => {
        if (link.url) {
            history.push(link.url);
        }
    }, [history, link.url]);

    return (
        <Section>
            {fields.heading && (
                <Typography
                    variant="h2"
                    style={{
                        textAlign: "center",
                        // @ts-expect-error It's a new css property
                        textWrap: "balance",
                    }}
                >
                    {fields.heading}
                </Typography>
            )}
            <Card href={link.url} onClick={onClick}>
                <TextContainer>
                    {fields.tag && <Tag>{fields.tag}</Tag>}
                    <Title>{fields.title}</Title>
                    <Time dateTime={new Date(fields.publishDate).toISOString()}>
                        <LocalizedDateTime
                            dateTime={new Date(fields.publishDate)}
                            options={{ year: "numeric", month: "short", day: "numeric" }}
                        />
                    </Time>
                </TextContainer>
                <Image src={fields.image} alt="" />
            </Card>
        </Section>
    );
};

const Section = styled.section`
    position: relative;
    display: flex;
    flex-direction: column;
    gap: ${rem(20)};
    background-color: ${colours.lightBlue50};
    margin-top: clamp(12px, -6.6667px + 5.8333vw, 68px);
    // Remove top margin if it's placed after a quote
    ${QuoteWidget} + &,  div[data-widget="Basic/Quote"] + div[data-widget] & {
        margin-top: 0;
    }
    padding: ${rem(20)} 0;
    @media screen and (min-width: ${breakpointWidth("lg")}) {
        padding-bottom: ${rem(60)};
    }
    z-index: 0;
    ::before {
        position: absolute;
        top: 0;
        left: 0;
        margin: 0 calc((100% - 100vw) / 2);
        width: 100vw;
        height: 100%;
        z-index: -1;
        display: block;
        content: "";
        background-color: ${colours.lightBlue50};
    }
    ::after {
        position: absolute;
        bottom: 100%;
        left: 0;
        right: 0;
        margin: 0 calc((100% - 100vw) / 2);
        width: 100vw;
        height: 12px;
        height: clamp(12px, -6.6667px + 5.8333vw, 68px);
        z-index: 1;
        display: block;
        content: "";
        background-color: ${colours.lightBlue50};
        clip-path: ellipse(70% 320% at 50% 320%);
    }
`;

const Card = styled.a`
    display: flex;
    flex-direction: column-reverse;
    background-color: ${colours.white};
    border-radius: ${rem(10)};
    overflow: hidden;
    @media screen and (min-width: ${breakpointWidth("lg")}) {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: ${rem(40)};
        align-items: center;
    }
`;

const TextContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${rem(10)};
    line-height: 1.5em;
    padding: ${rem(20)};
    @media screen and (min-width: ${breakpointWidth("lg")}) {
        padding: ${rem(20)} ${rem(60)};
    }
`;

const Title = styled.h2`
    font-weight: 700;
    margin: 0;
    padding: 0;
    line-height: 1.2em;
    font-size: ${rem(20)};
    font-family: ${bodyFont};
    color: ${colours.darkBlue};
    @media screen and (min-width: ${breakpointWidth("md")}) {
        font-size: ${rem(28)};
    }
    a:hover & {
        color: ${colours.green};
    }
`;

const Tag = styled.span`
    font-size: ${rem(15)};
    line-height: 1em;
    align-self: start;
    font-weight: 600;
    text-transform: uppercase;
    color: ${colours.darkBlue};
    background-color: ${colours.lightBlue};
    border-radius: ${rem(5)};
    padding: ${rem(8)} ${rem(5)};
`;

const Time = styled.time`
    font-size: ${rem(15)};
`;

const Image = styled.img`
    width: 100%;
    display: block;
    aspect-ratio: 4 / 3;
    object-fit: cover;
    @media screen and (min-width: ${breakpointWidth("md")}) {
        aspect-ratio: 2.5 / 1;
    }
    @media screen and (min-width: ${breakpointWidth("lg")}) {
        aspect-ratio: 4 / 3;
    }
`;

const articleSection: WidgetModule = {
    component: ArticleSection,
    definition: {
        group: "Basic",
        icon: "newspaper",
        fieldDefinitions: [
            {
                name: "heading",
                displayName: "Heading",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: "link",
                displayName: "Article link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                isRequired: true,
            },
            {
                name: "image",
                displayName: "Article image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: "title",
                displayName: "Article title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: "tag",
                displayName: "Article tag",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: "publishDate",
                displayName: "Article publish date",
                editorTemplate: "DateTimeField",
                defaultValue: new Date(),
                fieldType: "General",
                isRequired: true,
            },
        ],
    },
};

export default articleSection;
